import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Save, Upload, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading, ExportButton } from "@/components/Common";
import { SimpleSelect, dbOptions, matchesOption } from "@/components/Filters";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api, errMsg, notifyStockChanged } from "@/lib/api";
import { dateLabel } from "@/lib/format";

const comps = (x) => (Array.isArray(x?.components) ? x.components : []);
const contents = (x) => comps(x).map((c) => `${c.qty ?? 0}× ${c.name || "Unknown product"}`).join(", ");
const unmapped = (a) => !comps(a).length || comps(a).some((c) => !c.sku);
// Loads a list; on failure returns [] and reports the error instead of throwing.
const safeList = (req, onErr) => req.then((r) => { onErr(null); return Array.isArray(r.data) ? r.data : []; })
  .catch((e) => { onErr(errMsg(e)); return []; });

const LoadError = ({ msg, onRetry, testid }) => (
  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid={testid}>
    <span>Could not load: {msg}</span>
    <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testid}-retry`}>Retry</Button>
  </div>
);

const UnmappedNote = ({ list }) => !list.length ? null : (
  <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="asin-unmapped-note">
    <b>Unmapped ASINs ({list.length})</b> — no products linked, or a linked product no longer exists:{" "}
    <span className="font-mono">{list.slice(0, 12).map((a) => a.asin).join(", ")}{list.length > 12 ? "…" : ""}</span>{" "}
    <Link to="/asins" className="font-semibold text-primary underline" data-testid="asin-unmapped-link">Fix in ASIN Catalogue</Link>
  </div>
);

const DeductionPreview = ({ lines }) => (
  <div className="panel p-4" data-testid="asin-deduction-preview">
    <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Stock that will go OUT</div>
    {!lines.length && <div className="py-6 text-center text-sm text-muted-foreground">Enter units sold for an ASIN to see the deduction.</div>}
    <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
      {lines.map((l) => (
        <div key={l.id} className="flex items-center gap-2 py-1.5 text-xs">
          <span className="flex-1 truncate" title={l.name}>{l.name}</span>
          <span className="font-mono text-red-400">-{l.qty}</span>
          <span className={`w-14 text-right font-mono font-bold ${l.after < 0 ? "text-red-600" : "text-foreground"}`}>{l.after}</span>
        </div>
      ))}
    </div>
  </div>
);

const DaySales = ({ sales, isAdmin, onDelete }) => (
  <div className="panel mt-4 overflow-auto">
    <table className="data-table" data-testid="asin-sales-log">
      <thead><tr><th>ASIN</th><th>Seller SKU</th><th className="text-right">Units</th><th>WH</th><th>Stock deducted</th><th>By</th><th></th></tr></thead>
      <tbody>
        {sales.map((s) => (
          <tr key={s.id} data-testid={`asin-sale-row-${s.asin}`}>
            <td className="font-mono font-bold text-primary">{s.asin}</td><td className="text-xs max-w-[200px] truncate">{s.seller_sku}</td>
            <td className="num font-bold">{s.qty}</td><td className="text-xs">WH {s.warehouse}</td>
            <td className="text-xs max-w-[380px] truncate" title={contents(s)}>{contents(s)}</td>
            <td className="text-xs text-muted-foreground">{s.created_by}</td>
            <td>{isAdmin && <button onClick={() => onDelete(s)} className="p-1 text-muted-foreground hover:text-red-400" data-testid={`delete-asin-sale-${s.id}`}><Trash2 className="h-4 w-4" /></button>}</td>
          </tr>
        ))}
        {!sales.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground" data-testid="asin-sales-empty">No ASIN sales recorded for this date</td></tr>}
      </tbody>
    </table>
  </div>
);

export const AsinSalesPanel = ({ date, warehouse, refNo, stockRows }) => {
  const { isAdmin } = useAuth();
  const [asinErr, setAsinErr] = useState(null);
  const [salesErr, setSalesErr] = useState(null);
  const { data: asins, reload: reloadAsins } = useLoad(() => safeList(api.get("/asins"), setAsinErr));
  const { data: sales, reload } = useLoad(() => safeList(api.get("/asin-sales", { params: { date } }), setSalesErr), [date]);
  const [qty, setQty] = useState({});
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const groups = useMemo(() => dbOptions((asins || []).map((a) => a.group), "(No group)"), [asins]);
  const unmappedList = useMemo(() => (asins || []).filter(unmapped), [asins]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const has = (v) => (v || "").toLowerCase().includes(s);
    return (asins || []).filter((a) => matchesOption(a.group, group) && (!s || has(a.asin) || has(a.seller_sku) || has(a.group)));
  }, [asins, q, group]);
  const chosen = (asins || []).filter((a) => Number(qty[a.asin]) > 0);
  const lines = useMemo(() => {
    const stock = Object.fromEntries((stockRows || []).map((r) => [r.id, warehouse === "A" ? r.stock_a : r.stock_b]));
    const agg = {};
    chosen.forEach((a) => comps(a).forEach((c) => {
      agg[c.product_id] = agg[c.product_id] || { id: c.product_id, name: c.name || "Unknown product", qty: 0 };
      agg[c.product_id].qty += (Number(c.qty) || 0) * Number(qty[a.asin]);
    }));
    return Object.values(agg).map((l) => ({ ...l, after: (stock[l.id] ?? 0) - l.qty }));
  }, [chosen, qty, stockRows, warehouse]);

  const save = async () => {
    setBusy(true);
    try {
      const entries = chosen.map((a) => ({ asin: a.asin, qty: Number(qty[a.asin]) }));
      const { data } = await api.post("/asin-sales", { date, warehouse, ref: refNo, entries });
      toast.success(`${data.sales} ASIN sale(s) saved — ${data.movements} stock OUT entries for ${dateLabel(date)}`);
      setQty({});
      notifyStockChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/asin-sales/parse", fd);
      const rows = data?.rows || [], unknown = data?.unknown || [];
      setQty(Object.fromEntries(rows.filter((r) => r.known).map((r) => [r.asin, r.qty])));
      toast.success(`${rows.length - unknown.length} ASIN(s) filled from file — review and save`);
      if (unknown.length) toast.warning(`Not in catalogue: ${unknown.slice(0, 8).join(", ")}${unknown.length > 8 ? "…" : ""}`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete ASIN sale ${s.asin} ×${s.qty}? Stock will be added back.`)) return;
    try { await api.delete(`/asin-sales/${s.id}`); toast.success("ASIN sale deleted — stock restored"); notifyStockChanged(); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  if (!asins) return <Loading />;
  return (
    <div>
      {asinErr && <LoadError msg={asinErr} onRetry={reloadAsins} testid="asin-list-error" />}
      <UnmappedNote list={unmappedList} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ASIN, seller SKU, group…" className="pl-8 h-9" data-testid="asin-search-input" />
        </div>
        <SimpleSelect value={group} onChange={setGroup} testid="asin-group-select" className="h-9 w-full sm:w-64" options={[["all", "All product groups"], ...groups]} />
        <input ref={fileRef} type="file" accept=".csv,.txt,.tsv,.xlsx" className="hidden" onChange={upload} data-testid="asin-upload-input" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="asin-upload-btn"><Upload className="h-4 w-4" /> Upload sales file</Button>
        <ExportButton kind="asin_sales" params={{ month: date.slice(0, 7) }} label="Export month ASIN sales" />
        <span className="ml-auto font-mono text-xs text-muted-foreground">{filtered.length} / {asins.length} ASINs</span>
      </div>
      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <div className="panel overflow-auto max-h-[calc(100vh-430px)] min-h-[320px]">
          <table className="data-table" data-testid="asin-sales-table">
            <thead><tr><th>ASIN</th><th>Seller SKU / Size</th><th>Contents (per unit)</th><th className="text-right">Units sold</th></tr></thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className={comps(a).length ? "" : "opacity-50"} data-testid={`asin-row-${a.asin}`}>
                  <td className="font-mono text-xs font-bold">{a.asin}<div className="font-sans text-[10px] font-normal text-muted-foreground max-w-[160px] truncate">{(a.group || "").trim() || "(No group)"}</div></td>
                  <td className="text-xs max-w-[220px]"><div className="truncate">{a.seller_sku}</div><div className="text-muted-foreground truncate">{a.size}</div></td>
                  <td className="text-xs max-w-[360px] truncate" title={contents(a)}>{comps(a).length ? contents(a) : <span className="text-amber-300">No breakdown — add in ASIN Catalogue</span>}</td>
                  <td className="text-right">
                    <Input type="number" min="0" disabled={!comps(a).length} value={qty[a.asin] || ""} onChange={(e) => setQty((s) => ({ ...s, [a.asin]: e.target.value }))}
                      className={`h-8 w-20 text-right font-mono ml-auto ${qty[a.asin] ? "border-red-500 text-red-300" : ""}`} data-testid={`asin-qty-${a.asin}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DeductionPreview lines={lines} />
      </div>
      <div className="sticky bottom-0 mt-3 flex items-center justify-between rounded-md border border-border bg-card/95 p-3 backdrop-blur">
        <span className="text-sm text-muted-foreground" data-testid="asin-chosen-count">{chosen.length} ASIN(s) · {lines.length} product(s) · WH {warehouse} · {dateLabel(date)}</span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setQty({})} data-testid="asin-clear-btn">Clear</Button>
          <Button size="sm" onClick={save} disabled={busy || !chosen.length} data-testid="asin-save-btn"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save ASIN sales"}</Button>
        </div>
      </div>
      {salesErr && <div className="mt-4"><LoadError msg={salesErr} onRetry={reload} testid="asin-sales-error" /></div>}
      <DaySales sales={sales || []} isAdmin={isAdmin} onDelete={del} />
    </div>
  );
};
